import {RuleEventType, RulesLogic, TriggerRule, TriggerRules} from "../../../components/types";
import {FlowNode, FlowRuleContext, FlowRuleEngine} from "../../../components/flowGraph/types";

type SpotNodeData = {
    triggerRules?: TriggerRules;
};

const POSITIVE_EVENTS = new Set<RuleEventType>(["SPOT_TRIGGERED", "SPOT_FINISHED", "SPOT_FINISHED_FOR_MS", "AMBIENT_TRIGGERED", "AMBIENT_TIME_SINCE_TRIGGERED"]);
const NEGATIVE_EVENTS = new Set<RuleEventType>(["SPOT_NOT_TRIGGERED", "SPOT_NOT_FINISHED", "AMBIENT_NOT_TRIGGERED"]);

const evaluateRule = (rule: TriggerRule | undefined, context: FlowRuleContext) => {
    const refKey = rule?.spotKey || rule?.zoneKey;
    if (!refKey) {
        return false;
    }
    const knownNodeIds = new Set(context.nodes.map((node) => node.id));
    if (!knownNodeIds.has(refKey)) {
        // Ambient zone references won't be in the spot graph — treat as satisfied
        if (rule?.type === "Ambient") {
            return POSITIVE_EVENTS.has(rule.eventType);
        }
        return false;
    }
    if (POSITIVE_EVENTS.has(rule!.eventType)) {
        return context.pathSet.has(refKey);
    }
    if (NEGATIVE_EVENTS.has(rule!.eventType)) {
        return !context.pathSet.has(refKey);
    }
    return false;
};

export const createSpotRuleEngine = (): FlowRuleEngine => ({
    canReachNode: (node: FlowNode, context: FlowRuleContext) => {
        const rules = (node.data as SpotNodeData | undefined)?.triggerRules;
        if (!rules || !Array.isArray(rules.rules) || rules.rules.length === 0) {
            return true;
        }
        const logic = rules.logic ?? RulesLogic.AND;
        if (logic === RulesLogic.OR) {
            return rules.rules.some((rule) => evaluateRule(rule, context));
        }
        return rules.rules.every((rule) => evaluateRule(rule, context));
    }
});
